import { useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { CheckCircle } from '../../../components/Icons'
import { IParsedFeatures } from '../../../types/subscription'

import './styles.css'

const napsterLogo = '/images/naspter-view-logo.png'

const extraQueryParams = {}

interface PricingCardProps {
  onCheckout?: (
    price_id: PricingCardProps['priceId'],
    returnURL: string,
    cancelUrl: string,
  ) => void
  isAuthenticated: boolean
  link?: string
  priceId: string
  name: string
  price: number
  description: string
  features?: string
  per?: string
  hours?: string
  packCard?: boolean
  subsPlanName?: string
  freeSubsName?: string
  showBestValue?: boolean
  isLoading?: boolean
  isHardware?: boolean
  subPriceId?: string
}

// TODO: remove packCard as we have separate component

export function PricingCard({
  priceId,
  name,
  price,
  description,
  features,
  onCheckout,
  per,
  isAuthenticated,
  link,
  packCard,
  subsPlanName,
  freeSubsName,
  showBestValue,
  isLoading,
  isHardware,
  subPriceId,
}: PricingCardProps) {
  const dollarsAndCents = (price / 100).toFixed(2).split('.')

  const isActive = useMemo(() => {
    return subsPlanName && isAuthenticated && priceId === subPriceId
  }, [isAuthenticated, subsPlanName, name])

  const [isProgressing, setProgressing] = useState(false)

  const auth = useAuth()

  const parsedFeatures = useMemo<IParsedFeatures>(
    () => JSON.parse(features ?? '[]') as IParsedFeatures,
    [features],
  )
  console.log('parsedFeatures', parsedFeatures.isPopular)

  const handleClick = () => {
    setProgressing(true)
    if (auth.user) {
      const urlParams = new URLSearchParams(window.location.search)
      let nextUrl = urlParams.get('next_url')
      nextUrl =
        nextUrl === null || nextUrl === undefined || nextUrl === ''
          ? `${window.location.origin}/pricing`
          : nextUrl
      onCheckout?.(priceId, nextUrl, name)
      return
    }
    if (price === 0) {
      void auth.signinRedirect({
        extraQueryParams: { ...extraQueryParams, action: 'get-started' },
      })
      return
    }

    const fullPath = `/pricing-checkout?priceId=${priceId}`
    void auth.signinRedirect({
      state: {
        path: fullPath,
        returnUrl: '/pricing',
      },
      extraQueryParams: {
        ...extraQueryParams,
        action: 'get-started',
      },
    })
  }

  return (
    <div
      className={`pricing-card-container ${
        isHardware ? '!max-w-full !min-w-0' : ''
      }`}
    >
      <div
        className={`pricing-card py-6 px-6 ${isHardware ? 'pt-0 ' : ''}${
          (parsedFeatures?.isPopular && subsPlanName === freeSubsName) ||
          (parsedFeatures?.isPopular && !isAuthenticated) ||
          isActive ||
          (packCard && parsedFeatures?.isPopular)
            ? 'tc-mv-blur-brown'
            : 'tc-mv-blur'
        }`}
      >
        {isHardware ? (
          <>
            <img className="w-full h-auto" src="/images/banner-view.png" />
          </>
        ) : (
          <>
            <h3 className="pricing-card-name">
              <div className="flex items-center font-[NapsterSans]">
                <span className="font-['DM_Serif_Text']">{name}</span>
                {showBestValue && parsedFeatures?.isPopular && (
                  <div className="pricing-card-name-badge px-2 py-1 font-[NapsterSans] me-4 sm:text-base text-sm text-center">
                    Best Value
                  </div>
                )}
              </div>
              <img
                src={napsterLogo}
                alt="Napster View"
                className={`pricing-card-logo ${
                  parsedFeatures?.isPopular
                    ? 'sm:block hidden mb-[-20px]'
                    : 'hidden'
                }`}
              />
            </h3>
            <div className="pricing-card-price my-5 sm:my-2 mb-0">
              <span className="price">${dollarsAndCents[0]}</span>
              {!!per && <span className="period">/{per}</span>}
              <img
                src={napsterLogo}
                alt="Napster View"
                className={`pricing-card-logo ml-auto ${
                  parsedFeatures?.isPopular ? 'block sm:hidden' : 'hidden'
                }`}
              />
            </div>
            <p className="pricing-card-description">{description}</p>

            <div className="flex min-h-[60px] flex-col sm:flex-row sm:mt-0 mt-8">
              <ul className="pricing-card-features">
                {parsedFeatures?.items?.map((feature, index) => (
                  <li key={index} className="flex flex-col gap-2">
                    <span className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      {feature.html ? (
                        <span
                          className="text-sm text-white w-full"
                          dangerouslySetInnerHTML={{ __html: feature.html }}
                        />
                      ) : (
                        <span className="">{feature.text}</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <span className="pricing-card-button text-black bg-white">
          <AnimatedSpinner className="text-black bg-white" />
        </span>
      ) : link ? (
        isActive ? (
          <span className="pricing-card-button text-black bg-white">
            Current Plan
          </span>
        ) : (
          <a
            href={link}
            className="pricing-card-button text-black bg-white"
            target="_blank"
            rel="noreferrer noopener"
          >
            Subscribe
          </a>
        )
      ) : (
        <button
          className="pricing-card-button text-black bg-white"
          onClick={handleClick}
        >
          <span className="price-label p-2 text-black">
            {isAuthenticated
              ? packCard
                ? 'Buy Now'
                : 'Subscribe'
              : 'Get Started'}
          </span>
          {isProgressing ? (
            <AnimatedSpinner className="text-black bg-white" />
          ) : (
            ''
          )}
        </button>
      )}
    </div>
  )
}
